import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  TrendingUp,
  SwapHoriz,
  AccountBalance,
  Settings,
  CurrencyExchange,
  AttachMoney,
  Warning,
  Logout,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../store/authSlice';
import { SLOW_REFRESH_MS } from '../config/intervals';
import WebSocketBadge from './WebSocketBadge';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Trading Pairs', icon: <CurrencyExchange />, path: '/trading-pairs' },
  { text: 'Triggers', icon: <TrendingUp />, path: '/triggers' },
  { text: 'Trades', icon: <SwapHoriz />, path: '/trades' },
  { text: 'Exchanges', icon: <AccountBalance />, path: '/exchanges' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
  { text: 'Emergency Stop', icon: <Warning />, path: '/emergency-stop' },
];

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    // Persist collapsed state in localStorage
    const saved = localStorage.getItem('drawerCollapsed');
    return saved === 'true';
  });
  const [usdKrwRate, setUsdKrwRate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  const handleDrawerCollapse = () => {
    const newState = !drawerCollapsed;
    setDrawerCollapsed(newState);
    localStorage.setItem('drawerCollapsed', String(newState));
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Fetch USD/KRW rate
  useEffect(() => {
    const fetchUsdKrwRate = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/api/exchange-rate/`);
        if (response.ok) {
          const data = await response.json();
          console.log('USD/KRW data:', data);
          setUsdKrwRate(data.usd_krw);
        } else {
          console.error('Failed to fetch USD/KRW rate:', response.status);
        }
      } catch (error) {
        console.error('Error fetching USD/KRW rate:', error);
      }
    };

    // Fetch immediately
    fetchUsdKrwRate();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUsdKrwRate, SLOW_REFRESH_MS);

    return () => clearInterval(interval);
  }, []);

  const drawer = (
    <div>
      <Toolbar sx={{ justifyContent: drawerCollapsed ? 'center' : 'space-between', px: drawerCollapsed ? 1 : 2 }}>
        {!drawerCollapsed && (
          <Typography variant="h6" noWrap component="div">
            Crypto Arbitrage
          </Typography>
        )}
        <IconButton
          onClick={handleDrawerCollapse}
          size="small"
          aria-label={drawerCollapsed ? 'Expand navigation drawer' : 'Collapse navigation drawer'}
          aria-expanded={!drawerCollapsed}
        >
          {drawerCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={drawerCollapsed ? item.text : ''} placement="right" arrow>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerCollapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerCollapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!drawerCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <WebSocketBadge sx={{ mr: 1.5 }} />
          {usdKrwRate ? (
            <Chip
              icon={<AttachMoney />}
              label={`USD/KRW: ${parseFloat(usdKrwRate).toLocaleString('ko-KR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 'medium',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                mr: 2,
                '& .MuiChip-icon': {
                  color: 'inherit'
                }
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mr: 2 }}>
              Loading USD/KRW...
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user && (
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user.username?.[0]?.toUpperCase() || 'A'}
              </Avatar>
            )}
            <Button
              color="inherit"
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{ ml: 1 }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
        aria-label="navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          minWidth: 0,
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default Layout;